import { JobStatusResponse } from '@upright/common/internal/jobs/types';
import { ApiData } from '@upright/ui/api/common/hooks';
import { pickDefaultModelResult } from '../../../../../../../common/model/data-point-pickers';
import {
  ImpactLanguage,
  ModelEntityType,
  PossiblyNamedModelEntityReference,
} from '../../../../../../../common/model/input/types';
import {
  ModelEntityWithScores,
  ModelResultWithScores,
} from '../../../../../../../common/model/output/scores/model-entity-data/types';
import {
  FinalImpactScoreType,
  ImpactScoresByTIVV,
  NetScoreFormat,
} from '../../../../../../../common/model/output/scores/types';
import { Dimension, Valence, ValueChain } from '../../../../../../../common/model/types';
import type { ImpactIntervalsByTIVV } from '../../../../../../../common/model/output/scores/leena2-intervals';

/**
 * Data series for net impact chart
 *
 * (Note that the type is NOT compatible with ModelEntityData. The function below can be used for conversion)
 */
export interface ImpactChartDataSeries {
  impactScoresByTIVV?: ImpactScoresByTIVV;
  name?: string;
  id?: string;
  resultId?: number;
  type: ModelEntityType;
  color?: string;
  revenue?: number | undefined;
  /**
   * Optional pre-filter baseline scores for this series (e.g. the
   * pre-blacklist scores behind the Leena-2 debugger's FINAL scores).
   * Wherever the baseline exceeds the series' own score, the bar gets a
   * faded, striped "ghost" tail appended after its solid part — bar + tail
   * = baseline — so the removed share is visible without adding a separate
   * series row. Bars whose baseline matches their score are unaffected;
   * value labels keep showing the series' own score.
   */
  ghostScoresByTIVV?: ImpactScoresByTIVV;
  /**
   * Disable impact-bar clicks for this series only (no `onImpactClick` /
   * click URL, default cursor, no hover effect) while other series stay
   * clickable. Used when a series has no drilldown target — e.g. the
   * Leena-2 debugger's comparison series, whose old Net Impact results
   * don't use the universal impact structures the drilldown filters by.
   */
  clickDisabled?: boolean;
  /**
   * Leena-2 Clef results only (team users only — the API strips them
   * otherwise): the 80% interval of each category score, keyed and scaled like
   * `impactScoresByTIVV`. With the "Show Leena-2 uncertainty" view setting on,
   * impact bars that have one get a thin p10–p90 whisker and the interval in
   * their tooltip. Absent for every other result, which renders unchanged.
   */
  impactIntervalsByTIVV?: ImpactIntervalsByTIVV | null;
}

export function toImpactChartSeriesWithDefaultModelResult(data: ModelEntityWithScores): ImpactChartDataSeries {
  const modelResult: ModelResultWithScores = pickDefaultModelResult(data.modelResults);
  return resultToImpactChartSeries(data.name, data.id, data.type, modelResult);
}

export function resultToImpactChartSeries(
  name: string | undefined,
  id: string,
  type: ModelEntityType,
  modelResult: ModelResultWithScores | undefined,
  color?: string,
): ImpactChartDataSeries {
  const ret: ImpactChartDataSeries = {
    impactScoresByTIVV: modelResult?.impactScoresByTIVV,
    impactIntervalsByTIVV: modelResult?.impactIntervalsByTIVV,
    name,
    id,
    type,
    resultId: modelResult?.resultId,
    color,
  };

  return ret;
}

export interface NetImpactChartProps {
  data: ImpactChartDataSeries[] | undefined;
  valueFormatter?: (value: number) => string;
  getImpactClickUrl?: (
    valence: Valence,
    valueChain: ValueChain | undefined,
    impactCode: string,
    ref: PossiblyNamedModelEntityReference,
    resultId: number,
  ) => string;

  // OA: a hacky workaround to keep the old logic for mixin products.
  onImpactClick?: (
    valence: Valence,
    valueChain: ValueChain | undefined,
    impactCode: string,
    ref: PossiblyNamedModelEntityReference,
    resultId: number,
  ) => void;
  disableImpactClick?: boolean;

  onContributeClick?: () => void;
  width?: number;

  netScoreFormat?: NetScoreFormat;
  hideNetScore?: boolean;
  hideValueChainsSwitch?: boolean;
  showValueChainsSwitch?: boolean;
  title?: string;
  staticMode?: boolean;

  hideTitle?: boolean;
  bigFonts?: boolean;

  defaultShowValueChains?: boolean;
  showValueChains?: boolean;
  hideReleaseText?: boolean;
  onShowValueChainsChange?: (shown: boolean) => void;

  defaultScoreType?: FinalImpactScoreType;
  scoreTypeOverride?: FinalImpactScoreType;
  onScoreTypeChange?: (type: FinalImpactScoreType) => void;

  expandedOnDefault?: Dimension[];
  onExpandedChange?: (dimension: Dimension, newExpanded: boolean) => void;

  showLegend?: boolean; // Defaults to false
  scoreUnitSelectorPlacement?: 'left' | 'right'; // Defaults to left

  comparisonMode?: true;
  enableE2eTestIds?: true;
  hasFinishedLoading?: boolean;
  showRenderViewButton?: boolean;
  alignLineEdges?: boolean;
  version?: string;
  release?: string;
  contributingArticlesCount?: number;
  className?: string;
  negativeColor?: string;
  simplify?: boolean;
  positiveColor?: string;
  backgroundColor?: string;
  language?: ImpactLanguage;

  updateJobStatusRequest?: ApiData<JobStatusResponse>;

  // OA currently only used to pass this through to the render view modal to prevent double data loading.
  // A bit ugly yes, but will suffice for now.
  entityData?: Array<ModelEntityWithScores | undefined>;
}

export enum ScaleMode {
  DEFAULT,
  ABSOLUTE,
}

export interface ImpactChartFontSizes {
  columnTitles: number;
  impactTitles: number;
  dimensionTitles: number;
  dimensionNetScores: number;
  impactNetScores: number;
  impactScores: number;
  netScore: number;
  scoreTypeText: number;
  valueSetText: number;
  modelVersionText: number;
  actionText: number; // for suggest fixes
  legendText: number;
  inputsText: number;
}
